import {useCallback} from 'react';

import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {
    getAdvertisementImage,
    removeAdvertisementImage,
    updateAdvertisementImage,
} from 'src/openapi/admin/apis/ConferenceAdvertisementApi';
import {useAppDispatch} from 'src/store/hooks';
import {base64ToFile} from 'src/utils/base64ToFile';

import type {GetAdvertisementImage$Request} from 'src/openapi/admin/apis/ConferenceAdvertisementApi';
import type {SuccessPayload} from 'src/typedef';

export const useAdvertisementImage = ({conferenceId, settingsType}: GetAdvertisementImage$Request) => {
    const apiDispatch = useAppDispatch();

    const getDispatchAction = useCallback(async (signal: AbortSignal) => {
        const imageResponse = await apiDispatch(getAdvertisementImage({conferenceId, settingsType}, {signal}));

        if (imageResponse.payload.error) {
            return Promise.resolve({type: '', payload: imageResponse.payload});
        }

        const {content} = imageResponse.payload.data;
        const payload: SuccessPayload<File | null> = {
            success: true,
            error: false,
            data: content ? base64ToFile(content) : null,
        };
        return Promise.resolve({type: '', payload});
    }, [conferenceId, settingsType, apiDispatch]);

    const saveDispatchAction = useCallback(async (multipartFile: File | null, signal: AbortSignal) => {
        if (multipartFile) {
            const imageResponse = await apiDispatch(updateAdvertisementImage({
                conferenceId,
                settingsType,
                multipartFile,
            }, {signal}));

            return Promise.resolve({type: '', payload: imageResponse.payload});
        } else {
            const imageResponse = await apiDispatch(removeAdvertisementImage({
                conferenceId,
                settingsType,
            }, {signal}));

            return Promise.resolve({type: '', payload: imageResponse.payload});
        }
    }, [conferenceId, settingsType, apiDispatch]);

    return useCommonDataHook({
        initValue: null,
        getDispatchAction,
        saveDispatchAction,
    });
};
